<script setup lang="ts">
import { onMounted, onUnmounted, watch } from 'vue';
import { RouterView } from 'vue-router';
import Toast from 'primevue/toast';
import type { WsClientFrame } from '@neon-one/auctions-types';

import ConnectionLostBanner from '@/shell/components/ConnectionLostBanner.vue';
import ArchivedBanner from '@/shell/components/ArchivedBanner.vue';
import ErrorBoundary from '@/shell/error-boundary/ErrorBoundary.vue';
import { FeedbackFab } from '@neon-one/feedback-widget';
import { env } from '@/env';
import { useWs } from '@/shell/stores/ws';
import { useAdminSession } from '@/shell/stores/adminSession';
import { useCurrentAuction } from '@/shell/stores/currentAuction';
import { useToast } from '@/shell/toast';
import { subscribeToast } from '@/shell/toast/bus';
import { useSupervisorStore } from '@/features/supervisor';
import { useSessionKeepAlive } from '@/shell/auth/useSessionKeepAlive';
import { usePendo } from '@/shell/pendo';
import { useReconnectRefresh } from '@/shell/lib/useReconnectRefresh';

// Post-design-canonical integration (2026-05-14): the admin-wide chrome
// was AdminShell + Sidebar + TopBar. Both replaced by per-page shells
// supplied via the route components themselves (AuctionsIndexShell on
// the /auctions list, ShellPicker → BuilderShell on per-auction
// surfaces). App.vue keeps the cross-cutting plumbing (toast bus, WS
// connect, connection-lost banner, error boundary) and lets each page
// dictate its own chrome.
const ws = useWs();
const session = useAdminSession();
const currentAuction = useCurrentAuction();
const toast = useToast();
const keepAlive = useSessionKeepAlive();
usePendo();

function getAdminIdentity() {
  return {
    type: 'admin' as const,
    id: session.user?.sub ?? '',
    email: session.user?.email ?? undefined,
  };
}

function getAdminAuctionContext() {
  const ev = currentAuction.event;
  if (!ev) return null;
  return { id: ev.uuid, name: ev.name };
}

let unsubscribeToast: (() => void) | null = null;

// App-wide feature-flag live sync. The backend broadcasts a
// `feature_flag.changed` frame on the global `flags:all` channel whenever any
// evaluable flag change happens (toggle, override, scheduled flip). On receipt
// we force-refetch this admin's own evaluated client flags, and — if the
// supervisor management table is currently populated — refresh it too so a peer
// supervisor's live change is reflected without a manual reload.
const FLAGS_CHANNEL = 'flags:all';
let flagsSubscribed = false;

function onFlagsFrame(frame: WsClientFrame): void {
  if (frame.type !== 'feature_flag.changed') return;
  session.reloadFeatureFlags().catch(() => undefined);
  const supervisor = useSupervisorStore();
  if (supervisor.featureFlags.length > 0) {
    supervisor.loadFeatureFlags().catch(() => undefined);
  }
}

function subscribeFlags(): void {
  if (flagsSubscribed) return;
  ws.subscribe([FLAGS_CHANNEL], onFlagsFrame);
  flagsSubscribed = true;
}

function unsubscribeFlags(): void {
  if (!flagsSubscribed) return;
  ws.unsubscribe([FLAGS_CHANNEL], onFlagsFrame);
  flagsSubscribed = false;
}

useReconnectRefresh(() => session.reloadFeatureFlags());

// Mobile NAT timeouts and browser tab suspension can leave the socket
// in a zombie state — readyState says OPEN, but server-side DDB bindings
// point at a connectionId that no longer accepts frames. When the tab
// returns to the foreground after being hidden for more than 30 s, force
// a fresh socket so the admin channel binding is replayed under a new id.
let hiddenSince: number | null = null;
const ZOMBIE_THRESHOLD_MS = 30_000;
function onVisibilityChange(): void {
  if (typeof document === 'undefined') return;
  if (document.visibilityState === 'hidden') {
    hiddenSince = Date.now();
    return;
  }
  if (document.visibilityState !== 'visible') return;
  const hiddenMs = hiddenSince ? Date.now() - hiddenSince : 0;
  hiddenSince = null;
  if (hiddenMs < ZOMBIE_THRESHOLD_MS) return;
  if (!session.isAuthenticated) return;
  void ws.reconnect();
}

onMounted(() => {
  // Bridge non-Vue callers (axios interceptors, route guards) into
  // PrimeVue's toast service via the toast bus singleton.
  unsubscribeToast = subscribeToast(event => {
    toast[event.severity]({
      summary: event.summary,
      detail: event.detail,
      life: event.life,
    });
  });
  document.addEventListener('visibilitychange', onVisibilityChange);
});

// Only connect when an admin is authenticated. Connecting before login leaves
// the gateway in a tight 401 + exponential-backoff reconnect loop.
let authTransition = 0;
watch(
  () => session.isAuthenticated,
  async authenticated => {
    const transition = ++authTransition;
    if (authenticated) {
      await ws.connect().catch(() => undefined);
      // Bail if auth flipped (logout/unmount) while ws.connect() was pending,
      // otherwise a stale callback would restart keep-alive for a dead session.
      if (transition !== authTransition || !session.isAuthenticated) return;
      subscribeFlags();
      keepAlive.start();
    } else {
      unsubscribeFlags();
      ws.disconnect();
      keepAlive.stop();
    }
  },
  { immediate: true }
);

onUnmounted(() => {
  // Invalidate any in-flight auth-watch continuation (e.g. a pending
  // ws.connect()) so it fails the transition guard and cannot restart
  // keep-alive after we've torn it down below.
  ++authTransition;
  unsubscribeToast?.();
  unsubscribeToast = null;
  unsubscribeFlags();
  ws.disconnect();
  keepAlive.stop();
  document.removeEventListener('visibilitychange', onVisibilityChange);
});
</script>

<template>
  <ErrorBoundary>
    <div class="app-root neon-circuit">
      <ConnectionLostBanner />
      <!-- Route-scoped: every auction page, shell or not, shares this one ancestor. -->
      <ArchivedBanner />
      <RouterView />
      <Toast />
      <FeedbackFab
        v-if="env.feedbackWidget"
        spa-name="admin-web"
        :api-base-url="env.apiBaseUrl"
        :get-user-identity="getAdminIdentity"
        :get-auction-context="getAdminAuctionContext"
      />
    </div>
  </ErrorBoundary>
</template>

<style scoped>
.app-root {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: var(--app-background-primary);
  color: var(--app-text-primary);
}
</style>
